import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../../services/api";

export default function useInvoices(companyId, socket, enabled) {
  const [result, setResult] = useState({ invoices: [], count: 0, summary: null, companyId: null });
  const [page, setPage] = useState(0), [pageSize, updatePageSize] = useState(25);
  const [search, setSearch] = useState(""), [query, setQuery] = useState("");
  const [status, updateStatus] = useState("all");
  const [revision, setRevision] = useState(0), [loading, setLoading] = useState(enabled), [error, setError] = useState(false);
  const sequence = useRef(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  const setPageSize = value => { updatePageSize(value); setPage(0); };
  const setStatus = value => { updateStatus(value); setPage(0); };
  const clearFilters = () => { setSearch(""); setQuery(""); updateStatus("all"); setPage(0); };

  useEffect(() => {
    if (search.trim() === query) return undefined;
    const timer = setTimeout(() => { setPage(0); setQuery(search.trim()); }, 300);
    return () => clearTimeout(timer);
  }, [search, query]);

  useEffect(() => {
    if (!enabled || !companyId) { setLoading(false); return undefined; }
    const request = ++sequence.current, cancel = axios.CancelToken.source();
    setLoading(true); setError(false);
    api.get("/invoices", { params: { searchParam: query, pageNumber: page + 1, pageSize, status }, timeout: 20000, cancelToken: cancel.token })
      .then(({ data }) => {
        if (request !== sequence.current) return;
        if (!Array.isArray(data.invoices) || !Number.isInteger(Number(data.count)) || Number(data.count) < 0) throw new Error("Invalid invoices response");
        const count = Number(data.count), lastPage = Math.max(0, Math.ceil(count / pageSize) - 1);
        if (page > lastPage) { setPage(lastPage); return; }
        setResult({ invoices: data.invoices, count, summary: data.summary || null, companyId });
      })
      .catch(err => { if (request === sequence.current && !axios.isCancel(err)) setError(true); })
      .finally(() => { if (request === sequence.current) setLoading(false); });
    return () => { sequence.current = request + 1; cancel.cancel(); };
  }, [companyId, enabled, page, pageSize, query, status, revision]);

  useEffect(() => {
    if (!enabled || !companyId) return undefined;
    let timer;
    const scheduleRefresh = () => {
      if (!timer) timer = setTimeout(() => { timer = null; refresh(); }, 250);
    };
    const visibility = () => { if (document.visibilityState === "visible") scheduleRefresh(); };
    const payment = data => { if (data?.action === "CONCLUIDA") scheduleRefresh(); };
    const event = `company-${companyId}-payment`;
    window.addEventListener("focus", scheduleRefresh);
    document.addEventListener("visibilitychange", visibility);
    if (socket) socket.on(event, payment);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", scheduleRefresh);
      document.removeEventListener("visibilitychange", visibility);
      if (socket) socket.off(event, payment);
    };
  }, [companyId, enabled, socket, refresh]);

  const visible = enabled && result.companyId === companyId && !error;
  return { invoices: visible ? result.invoices : [], count: visible ? result.count : 0, summary: visible ? result.summary : null,
    page, setPage, pageSize, setPageSize, search, setSearch, status, setStatus, clearFilters, loading, error, refresh };
}